import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useLicense } from './LicenseContext';

// ── Audio alert ────────────────────────────────────────────────────────────────
function playAlarmBeep(severity: string) {
  if (localStorage.getItem('lemai_mute_alarms') === '1') return;
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    // critical: 880Hz × 3 pulses, high: 660Hz × 2 pulses, medium: 440Hz × 1 pulse
    const count = severity === 'critical' ? 3 : severity === 'medium' ? 1 : 2;
    const freq  = severity === 'critical' ? 880 : severity === 'medium' ? 440 : 660;
    const vol   = severity === 'medium' ? 0.25 : 0.4;
    for (let i = 0; i < count; i++) {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      const t = ctx.currentTime + i * 0.35;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(vol, t + 0.02);
      gain.gain.linearRampToValueAtTime(0, t + 0.25);
      osc.start(t);
      osc.stop(t + 0.3);
    }
    // Close context after sounds finish
    setTimeout(() => ctx.close(), (count * 350) + 500);
  } catch { /* silently fail if AudioContext unavailable */ }
}

// ── Browser notification ───────────────────────────────────────────────────────
function fireBrowserNotification(alarm: LiveAlarm) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(`⚠ ${alarm.severity.toUpperCase()} ALARM — ${alarm.type}`, {
      body: `${alarm.site} · ${alarm.location}`,
      icon: '/logo192.png',
      tag: alarm.id,
      requireInteraction: alarm.severity === 'critical',
    });
  } catch { /* ignore — some browsers block in certain contexts */ }
}

export type AlarmSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface LiveAlarm {
  id: string;
  type: string;
  site: string;
  location: string;
  severity: AlarmSeverity;
  timestamp: string;  // ISO or HH:mm:ss
  tsMs: number;       // Date.now() at receipt — used for SLA timers
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  closedAt?: string;
  actionsTaken?: string;
  sop?: string[];
  escalated?: boolean;
  // Integration source fields
  source?: string;      // 'ArmorIP' | 'Protege GX' | 'Demo' etc.
  cameraId?: string;    // direct camera link from source system
  description?: string; // raw alarm message from source system
  repeatCount?: number; // alarm compression: how many times this event repeated
  lastSeenTs?: string;  // timestamp of most recent repeat
  slaBreach?: boolean;  // true when SLA breach notification received from server
}

export type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface AlarmSocketCtx {
  alarms: LiveAlarm[];
  wsStatus: WsStatus;
  acknowledge: (id: string, operator: string, actions: string) => void;
  closeAlarm: (id: string) => void;
  audioEnabled: boolean;
  setAudioEnabled: (v: boolean) => void;
}

const Ctx = createContext<AlarmSocketCtx>({
  alarms: [],
  wsStatus: 'disconnected',
  acknowledge: () => {},
  closeAlarm: () => {},
  audioEnabled: true,
  setAudioEnabled: () => {},
});

const SOP_MAP: Record<string, string[]> = {
  'Duress Button': [
    'Review camera feed at alarm location immediately',
    'Attempt contact via intercom — do NOT enter alone',
    'Notify site supervisor — do not wait for confirmation',
    'Dispatch nearest guard officer to location',
    'If no response within 2 min — contact Police (000)',
    'Document all actions, times and observations in job log',
  ],
  'Door Forced Open': [
    'Pull up cameras at affected location',
    'Lock down adjacent access points via access control system',
    'Dispatch guard for physical inspection',
    'If intrusion confirmed — escalate to Incident, notify police',
    'Document and photograph if safe to do so',
  ],
  'Perimeter Breach': [
    'Check perimeter cameras — confirm real vs. false alarm',
    'Notify on-site guard for patrol sweep',
    'Check for gate/door left open nearby',
    'If confirmed — lock down perimeter, escalate to supervisor',
  ],
  'Camera Tamper': [
    'Verify tamper via neighbouring camera',
    'Dispatch guard to physically inspect camera',
    'If vandalism confirmed — log incident, notify supervisor',
    'Raise service request for camera repair',
  ],
  'Fire Panel Input': [
    'IMMEDIATELY notify Fire & Rescue NSW (000)',
    'Begin site evacuation procedures',
    'Do NOT use lifts — use emergency exits only',
    'Account for all personnel at muster point',
    'Do not re-enter until fire brigade all-clear',
  ],
  'default': [
    'Review camera footage at alarm location',
    'Assess severity — confirm real vs. false alarm',
    'Dispatch guard if required',
    'Document actions taken in job log',
  ],
};

function sopFor(type: string): string[] {
  return SOP_MAP[type] ?? SOP_MAP['default'];
}

// Seed alarms for demo/fallback
const now = Date.now();
const SEED_ALARMS: LiveAlarm[] = [
  {
    id: 'LA-001', type: 'Duress Button',    site: 'Town Hall',      location: 'Control Room',
    severity: 'critical', timestamp: '08:41', tsMs: now - 18 * 60000,
    acknowledged: false, sop: sopFor('Duress Button'),
  },
  {
    id: 'LA-002', type: 'Perimeter Breach', site: 'Civic Centre',   location: 'North Fence',
    severity: 'high',     timestamp: '08:22', tsMs: now - 37 * 60000,
    acknowledged: false, sop: sopFor('Perimeter Breach'),
  },
  {
    id: 'LA-003', type: 'Camera Tamper',    site: 'Aquatic Centre', location: 'Plant Room',
    severity: 'high',     timestamp: '07:15', tsMs: now - 84 * 60000,
    acknowledged: true,  acknowledgedBy: 'J. Okafor', acknowledgedAt: '07:17',
    actionsTaken: 'Guard dispatched, camera physically inspected, no damage found.',
    sop: sopFor('Camera Tamper'),
  },
];

// ── Socket.IO client loader ──────────────────────────────────────────────────
// Dynamically loads the Socket.IO JS client from the backend server.
// This avoids adding it to package.json — the server always has it available
// at /socket.io/socket.io.js (served automatically by flask-socketio).

declare global {
  interface Window { io?: any; }
}

function loadSocketIO(): Promise<any> {
  return new Promise((resolve, reject) => {
    if (window.io) { resolve(window.io); return; }
    const script = document.createElement('script');
    script.src = '/socket.io/socket.io.js';
    script.onload = () => { if (window.io) resolve(window.io); else reject(new Error('socket.io not found on window')); };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// Compress active alarms with the same type+site+location into a single entry.
// The earliest alarm (lowest tsMs) is kept as the canonical entry; later ones
// increment its repeatCount and update lastSeenTs.
function compressLiveAlarms(list: LiveAlarm[]): LiveAlarm[] {
  const groupKey = (a: LiveAlarm) => `${a.type}||${a.site}||${a.location}`;
  const groups = new Map<string, LiveAlarm[]>();
  list.forEach(a => {
    if (a.acknowledged || a.closedAt) return; // don't compress resolved alarms
    const k = groupKey(a);
    groups.set(k, [...(groups.get(k) || []), a]);
  });
  const absorbed = new Set<string>();
  const out: LiveAlarm[] = [];
  list.forEach(a => {
    if (absorbed.has(a.id)) return;
    if (!a.acknowledged && !a.closedAt) {
      const k = groupKey(a);
      const group = groups.get(k) || [a];
      if (group.length > 1) {
        // Primary = earliest received (lowest tsMs = most stable id to ACK against)
        const primary = group.reduce((p, c) => c.tsMs < p.tsMs ? c : p);
        const totalCount = group.reduce((s, g) => s + (g.repeatCount || 1), 0);
        const latestTs   = group.reduce((p, c) => c.tsMs > p.tsMs ? c : p).timestamp;
        out.push({ ...primary, repeatCount: totalCount, lastSeenTs: latestTs });
        group.forEach(g => absorbed.add(g.id));
        return;
      }
    }
    absorbed.add(a.id);
    out.push(a);
  });
  return out;
}

function mergeAlarms(existing: LiveAlarm[], incoming: LiveAlarm[]): LiveAlarm[] {
  const ids = new Set(existing.map(a => a.id));
  const novel = incoming.filter(a => !ids.has(a.id));
  if (novel.length === 0) return existing;  // same reference — no re-render
  const merged = [...novel, ...existing].slice(0, 500);
  return compressLiveAlarms(merged);
}

export function AlarmSocketProvider({ children }: { children: React.ReactNode }) {
  const { isDemoOrg } = useLicense();
  const [alarms, setAlarms] = useState<LiveAlarm[]>(() => isDemoOrg ? SEED_ALARMS : []);
  const [wsStatus, setWsStatus] = useState<WsStatus>('disconnected');
  const socketRef = useRef<any>(null);
  const [audioEnabled, setAudioEnabledState] = useState<boolean>(
    () => localStorage.getItem('lemai_mute_alarms') !== '1'
  );
  const setAudioEnabled = useCallback((v: boolean) => {
    setAudioEnabledState(v);
    localStorage.setItem('lemai_mute_alarms', v ? '0' : '1');
  }, []);

  // Request browser notification permission once on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Reset seed data when org switches
  useEffect(() => {
    setAlarms(isDemoOrg ? SEED_ALARMS : []);
  }, [isDemoOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper — fetch stored alarms from REST and merge into state
  const fetchStoredAlarms = useCallback(() => {
    if (isDemoOrg) return;
    const token = localStorage.getItem('lemai_token') || sessionStorage.getItem('lemai_token') || '';
    if (!token) return;
    fetch('/api/armorip/alarms', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.alarms?.length) return;
        const stored: LiveAlarm[] = data.alarms.map((a: any) => ({
          ...a, tsMs: a.tsMs || Date.now(), sop: sopFor(a.type || ''),
        }));
        setAlarms(prev => mergeAlarms(prev, stored));
      })
      .catch(() => {});
  }, [isDemoOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic REST poll — runs every 30 s regardless of socket state.
  // This is the fallback so alarms always arrive even if Socket.IO is down.
  useEffect(() => {
    if (isDemoOrg) return;
    const t = setInterval(fetchStoredAlarms, 5_000);
    return () => clearInterval(t);
  }, [fetchStoredAlarms, isDemoOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let socket: any = null;
    let destroyed = false;

    // Fetch on start in case socket.io never loads (e.g. network issue)
    fetchStoredAlarms();

    async function connect() {
      try {
        setWsStatus('connecting');
        const io = await loadSocketIO();
        if (destroyed) return;

        const token = localStorage.getItem('lemai_token') || sessionStorage.getItem('lemai_token') || '';
        socket = io('/', {
          transports: ['websocket', 'polling'],
          auth: { token },
          query: { token },
          reconnection: true,
          reconnectionDelay: 3000,
          reconnectionDelayMax: 30000,
        });
        socketRef.current = socket;

        socket.on('connect', () => {
          if (!destroyed) {
            setWsStatus('connected');
            // Fetch stored alarms on every connect/reconnect — this is the
            // reliable trigger because it means we have a valid auth token.
            fetchStoredAlarms();
          }
        });
        socket.on('disconnect', () => { if (!destroyed) setWsStatus('disconnected'); });
        socket.on('connect_error', () => { if (!destroyed) setWsStatus('error'); });

        socket.on('alarm_new', (data: any) => {
          if (destroyed) return;
          const raw = data.alarm || data;
          const alarm: LiveAlarm = {
            id:          raw.id || `sio-${Date.now()}`,
            type:        raw.type || 'Unknown',
            site:        raw.site || 'Unknown',
            location:    raw.location || 'Unknown',
            severity:    (raw.severity || 'warning') as AlarmSeverity,
            timestamp:   raw.timestamp || new Date().toTimeString().slice(0, 8),
            tsMs:        raw.tsMs || Date.now(),
            acknowledged: raw.acknowledged || false,
            sop:         sopFor(raw.type || ''),
            source:      raw.source,
            cameraId:    raw.cameraId,
            description: raw.description,
          };
          setAlarms(prev => {
            if (prev.some(a => a.id === alarm.id)) return prev;
            // Sound + browser notification for new unacknowledged alarms
            if (!alarm.acknowledged && (alarm.severity === 'critical' || alarm.severity === 'high' || alarm.severity === 'medium')) {
              playAlarmBeep(alarm.severity);
              if (alarm.severity !== 'medium') fireBrowserNotification(alarm);
            }
            return compressLiveAlarms([alarm, ...prev].slice(0, 500));
          });
        });

        socket.on('alarm_ack', (data: any) => {
          if (destroyed) return;
          setAlarms(prev => prev.map(a =>
            a.id === data.id ? { ...a, acknowledged: true, acknowledgedBy: data.operator, acknowledgedAt: data.at } : a
          ));
        });

        socket.on('alarm_list', (data: any) => {
          if (destroyed) return;
          const incoming: LiveAlarm[] = (data.alarms ?? []).map((a: any) => ({
            ...a, tsMs: a.tsMs || Date.now(), sop: sopFor(a.type),
          }));
          setAlarms(prev => mergeAlarms(prev, incoming));
        });

        socket.on('alarm_close', (data: any) => {
          if (destroyed) return;
          setAlarms(prev => prev.map(a => a.id === data.id ? { ...a, closedAt: data.at } : a));
        });

        socket.on('alarm_sla_breach', (data: any) => {
          if (destroyed) return;
          setAlarms(prev => prev.map(a => a.id === data.id ? { ...a, slaBreach: true } : a));
        });

        socket.on('alarm_escalation', (data: any) => {
          if (destroyed) return;
          setAlarms(prev => prev.map(a => a.id === data.alarmId ? { ...a, escalated: true } : a));
          // Fire browser notification for escalations
          try {
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`⚠ ESCALATION — ${data.type || 'Alarm'}`, {
                body: `${data.site} · ${data.reason} (${data.ageMinutes} min unacknowledged)`,
                icon: '/logo192.png',
                requireInteraction: true,
              });
            }
          } catch { /* ignore */ }
        });

        socket.on('bulk_close', () => {
          if (destroyed) return;
          // Server bulk-closed old alarms — mark them in state
          const twoHoursAgo = Date.now() - 2 * 3600 * 1000;
          setAlarms(prev => prev.map(a =>
            (!a.acknowledged && a.tsMs && a.tsMs < twoHoursAgo) ? { ...a, acknowledged: true, closedAt: new Date().toLocaleTimeString() } : a
          ));
        });

        socket.on('bulk_alarm_action', (data: { ids: string[], action: string, operator: string, at: string }) => {
          if (destroyed) return;
          setAlarms(prev => prev.map(alarm => {
            if (!data.ids.includes(String(alarm.id)) && !data.ids.includes((alarm as any).sourceId)) return alarm;
            if (data.action === 'acknowledge') {
              return { ...alarm, status: 'acknowledged' as any, acknowledged: true, acknowledgedBy: data.operator, acknowledgedAt: data.at, ackedMs: Date.now() };
            }
            if (data.action === 'close') {
              return { ...alarm, status: 'closed' as any, closedAt: data.at, closedBy: data.operator };
            }
            return alarm;
          }));
        });

      } catch (err) {
        console.warn('AlarmSocket: Socket.IO unavailable, alarms via REST polling only', err);
        if (!destroyed) setWsStatus('disconnected');
      }
    }

    connect();

    return () => {
      destroyed = true;
      if (socket) { socket.disconnect(); socket.removeAllListeners(); }
      socketRef.current = null;
    };
  }, [fetchStoredAlarms]); // eslint-disable-line react-hooks/exhaustive-deps

  const acknowledge = useCallback((id: string, operator: string, actions: string) => {
    const at = new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    setAlarms(prev => prev.map(a =>
      a.id === id ? { ...a, acknowledged: true, acknowledgedBy: operator, acknowledgedAt: at, actionsTaken: actions } : a
    ));
    if (socketRef.current?.connected) {
      socketRef.current.emit('ack', { id, operator, actions, at });
    }
    const token = localStorage.getItem('lemai_token');
    fetch(`/api/armorip/alarms/${id}/acknowledge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ operator, actions, at }),
    }).catch(() => {});
  }, []);

  const closeAlarm = useCallback((id: string) => {
    const at = new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    setAlarms(prev => prev.map(a => a.id === id ? { ...a, closedAt: at } : a));
    if (socketRef.current?.connected) {
      socketRef.current.emit('close', { id, at });
    }
    const token = localStorage.getItem('lemai_token');
    fetch(`/api/armorip/alarms/${id}/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ at }),
    }).catch(() => {});
  }, []);

  return (
    <Ctx.Provider value={{ alarms, wsStatus, acknowledge, closeAlarm, audioEnabled, setAudioEnabled }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAlarmSocket() { return useContext(Ctx); }
